import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMultiStepForm } from '../../../ui-components/base/MultiStepForm';
import SelectDropdown from '../../../ui-components/base/SelectDropdown';
import styles from './PreviewPublishStep.module.scss';
import LiveBannerLimitAlert from '../LiveBannerLimitAlert';
import { getLiveBannerThreshold, LiveBannerLimitContext } from '../liveBannerLimit';

const PreviewPublishStep = () => {
  const { allStepData, setStepValid } = useMultiStepForm();
  const hasReachedLiveLimit = useContext(LiveBannerLimitContext);

  // Get market languages from Redux
  const marketLanguagesData = useSelector((state) => state.commonData?.marketLanguages);
  const remoteConfig = useSelector((state) => state.uiState.remoteConfig);

  // Extract step data
  const bannerInformationData = allStepData[0] || {};
  const translationData = allStepData[1] || {};
  const countryTargetingData = allStepData[2] || {};

  const isButtonLinkMode = bannerInformationData.bannerType === 'button';
  const translations = translationData.translations || {};

  const liveBannerThreshold = getLiveBannerThreshold(remoteConfig);

  // Build language options from translations
  const languageOptions = useMemo(() => {
    const primaryLocale = marketLanguagesData?.marketDefaultLocale;
    const options = [];

    if (primaryLocale) {
      options.push({
        value: primaryLocale.locale,
        label: primaryLocale.title || 'English',
      });
    }

    // Add secondary languages that have translations
    Object.keys(translations).forEach((locale) => {
      if (locale !== primaryLocale?.locale) {
        const localeData = marketLanguagesData?.otherLocales?.find(l => l.locale === locale);
        if (localeData) {
          options.push({
            value: locale,
            label: localeData.title,
          });
        }
      }
    });

    return options;
  }, [translations, marketLanguagesData]);

  // Default to primary language
  const [selectedLanguage, setSelectedLanguage] = useState(
    languageOptions[0]?.value || ''
  );

  // Update selected language when options change
  useEffect(() => {
    if (!selectedLanguage && languageOptions.length > 0) {
      setSelectedLanguage(languageOptions[0].value);
    }
  }, [languageOptions]);

  // Get content for selected language
  const getLocalizedContent = () => {
    const primaryLocale = marketLanguagesData?.marketDefaultLocale?.locale;

    // If primary language selected, use data from BannerInformationStep
    if (selectedLanguage === primaryLocale) {
      return {
        title: bannerInformationData.title || '',
        description: bannerInformationData.description || '',
        primaryButtonText: bannerInformationData.primaryButtonText || '',
        secondaryButtonText: bannerInformationData.secondaryButtonText || '',
      };
    }

    // Otherwise, use translation data
    const translation = translations[selectedLanguage] || {};
    return {
      title: translation.title || bannerInformationData.title || '',
      description: translation.description || bannerInformationData.description || '',
      primaryButtonText: translation.primaryButtonText || bannerInformationData.primaryButtonText || '',
      secondaryButtonText: translation.secondaryButtonText || bannerInformationData.secondaryButtonText || '',
    };
  };

  const content = getLocalizedContent();

  // Determine theme based on button text color
  const buttonTextColor = bannerInformationData.buttonColor || 'white'; // 'white' or 'black'
  const isBlackTheme = buttonTextColor === 'black';

  // Get image URLs
  const desktopImageUrl = useMemo(() => {
    if (bannerInformationData.desktopBannerImage) {
      // Check if it's a File object or an existing image reference
      if (bannerInformationData.desktopBannerImage instanceof File) {
        return URL.createObjectURL(bannerInformationData.desktopBannerImage);
      } else if (bannerInformationData.desktopBannerImage.href) {
        // It's an existing image from the API
        return bannerInformationData.desktopBannerImage.href;
      }
    }
    return null;
  }, [bannerInformationData.desktopBannerImage]);

  const mobileImageUrl = useMemo(() => {
    if (bannerInformationData.mobileBannerImage) {
      if (bannerInformationData.mobileBannerImage instanceof File) {
        return URL.createObjectURL(bannerInformationData.mobileBannerImage);
      } else if (bannerInformationData.mobileBannerImage.href) {
        return bannerInformationData.mobileBannerImage.href;
      }
    }
    return desktopImageUrl; // Fallback to desktop image
  }, [bannerInformationData.mobileBannerImage, desktopImageUrl]);

  // Always valid for preview step - re-validate on any data change to ensure it stays valid after errors
  useEffect(() => {
    setStepValid(true);
  }, []);

  // Cleanup URLs on unmount (only for blob URLs)
  useEffect(() => {
    return () => {
      if (desktopImageUrl && desktopImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(desktopImageUrl);
      }
      if (mobileImageUrl && mobileImageUrl.startsWith('blob:') && mobileImageUrl !== desktopImageUrl) {
        URL.revokeObjectURL(mobileImageUrl);
      }
    };
  }, []);

  return (
    <div className={styles.previewStep}>
      {/* Show the alert if banner threshold limit is exceeded */}
      {hasReachedLiveLimit && <LiveBannerLimitAlert threshold={liveBannerThreshold} />}

      {/* Header with Title and Language Selector */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>Preview & Publish</h2>
        </div>
        <div className={styles.headerRight}>
          {languageOptions.length > 0 && (
            <SelectDropdown
              options={languageOptions}
              value={selectedLanguage}
              onChange={(value) => setSelectedLanguage(value)}
              placeholder="Select Language"
            />
          )}
        </div>
      </div>

      {/* Device Previews */}
      <div className={styles.previewsContainer}>
        {/* Desktop Preview */}
        <div className={styles.deviceSection}>
          <h3 className={styles.deviceLabel}>Desktop</h3>
          <div className={styles.desktopPreview}>
            {desktopImageUrl && (
              <div className={styles.bannerContainer}>
                <img
                  src={desktopImageUrl}
                  alt="Desktop Banner"
                  className={styles.bannerImage}
                />
                <div className={styles.contentOverlay}>
                  <div className={`${styles.textContainer} ${isBlackTheme ? styles.themeBlack : ''}`}>
                    {content.title && (
                      <h2 className={`${styles.bannerTitle} ${isBlackTheme ? styles.themeBlack : ''}`}>
                        {content.title}
                      </h2>
                    )}
                    {content.description && (
                      <p className={`${styles.bannerDescription} ${isBlackTheme ? styles.themeBlack : ''}`}>
                        {content.description}
                      </p>
                    )}
                  </div>
                  {isButtonLinkMode && (content.primaryButtonText || content.secondaryButtonText) && (
                    <div className={styles.buttonGroup}>
                      {content.primaryButtonText && (
                        <button
                          className={`${styles.primaryButton} ${isBlackTheme ? styles.themeBlack : ''}`}
                          type="button"
                        >
                          {content.primaryButtonText}
                        </button>
                      )}
                      {content.secondaryButtonText && (
                        <button
                          className={`${styles.secondaryButton} ${isBlackTheme ? styles.themeBlack : ''}`}
                          type="button"
                        >
                          {content.secondaryButtonText}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {!desktopImageUrl && (
              <div className={styles.placeholderContainer}>
                <p className={styles.placeholderText}>No desktop image uploaded</p>
              </div>
            )}
          </div>
        </div>

        {/* Tablet Preview */}
        <div className={styles.deviceSection}>
          <h3 className={styles.deviceLabel}>Tablet</h3>
          <div className={styles.tabletPreview}>
            {desktopImageUrl && (
              <div className={styles.bannerContainer}>
                <img
                  src={desktopImageUrl}
                  alt="Tablet Banner"
                  className={styles.bannerImage}
                />
                <div className={styles.contentOverlay}>
                  <div className={`${styles.textContainer} ${styles.tabletAndMobile} ${isBlackTheme ? styles.themeBlack : ''}`}>
                    <h2 className={`${styles.bannerTitle} ${isBlackTheme ? styles.themeBlack : ''}`}>
                      {content.title}
                    </h2>
                    {content.description && (
                      <p className={`${styles.bannerDescription} ${isBlackTheme ? styles.themeBlack : ''}`}>
                        {content.description}
                      </p>
                    )}
                  </div>

                  {isButtonLinkMode && (content.primaryButtonText || content.secondaryButtonText) && (
                    <div className={styles.buttonGroup}>
                      {content.primaryButtonText && (
                        <button
                          className={`${styles.primaryButton} ${isBlackTheme ? styles.themeBlack : ''}`}
                          type="button"
                        >
                          {content.primaryButtonText}
                        </button>
                      )}
                      {content.secondaryButtonText && (
                        <button
                          className={`${styles.secondaryButton} ${isBlackTheme ? styles.themeBlack : ''}`}
                          type="button"
                        >
                          {content.secondaryButtonText}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {!desktopImageUrl && (
              <div className={styles.placeholderContainer}>
                <p className={styles.placeholderText}>No tablet image uploaded</p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Preview */}
        <div className={styles.deviceSection}>
          <h3 className={styles.deviceLabel}>Mobile</h3>
          <div className={styles.mobilePreview}>
            {mobileImageUrl && (
              <div className={styles.bannerContainer}>
                <img
                  src={mobileImageUrl}
                  alt="Mobile Banner"
                  className={styles.bannerImage}
                />
                <div className={styles.contentOverlay}>
                  <div className={`${styles.textContainer} ${styles.tabletAndMobile} ${isBlackTheme ? styles.themeBlack : ''}`}>
                    <h2 className={`${styles.bannerTitle} ${isBlackTheme ? styles.themeBlack : ''}`}>
                      {content.title}
                    </h2>
                    {content.description && (
                      <p className={`${styles.bannerDescription} ${isBlackTheme ? styles.themeBlack : ''}`}>
                        {content.description}
                      </p>
                    )}
                  </div>

                  {isButtonLinkMode && (content.primaryButtonText || content.secondaryButtonText) && (
                    <div className={styles.buttonGroup}>
                      {content.primaryButtonText && (
                        <button
                          className={`${styles.primaryButton} ${isBlackTheme ? styles.themeBlack : ''}`}
                          type="button"
                        >
                          {content.primaryButtonText}
                        </button>
                      )}
                      {content.secondaryButtonText && (
                        <button
                          className={`${styles.secondaryButton} ${isBlackTheme ? styles.themeBlack : ''}`}
                          type="button"
                        >
                          {content.secondaryButtonText}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {!mobileImageUrl && (
              <div className={styles.placeholderContainer}>
                <p className={styles.placeholderText}>No mobile image uploaded</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewPublishStep;
